import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import {
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  Box,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { AdvancingOrg } from '../types';
import AdvancingOrgModal from './AdvancingOrgModal';

interface AdvancingOrgSectionProps {
  selectedAlias?: string;
  userAlias?: string;
}

const AdvancingOrgSection: React.FC<AdvancingOrgSectionProps> = ({ selectedAlias, userAlias }) => {
  const [items, setItems] = useState<AdvancingOrg[]>([]);
  const [filteredItems, setFilteredItems] = useState<AdvancingOrg[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<AdvancingOrg | null>(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const hasFetched = useRef(false);

  useEffect(() => {
    // Reset hasFetched when selectedAlias changes
    hasFetched.current = false;

    // Only fetch when we have a selected direct report (manager view)
    if (selectedAlias && userAlias) {
      fetchItems();
    } else {
      // Clear data when no direct report is selected
      setItems([]);
      setLoading(false);
    }
  }, [selectedAlias, userAlias]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const filtered = items.filter(
      (item) =>
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.type && item.type.toLowerCase().includes(searchTerm.toLowerCase())) ||
        format(new Date(item.date), 'MMMM d, yyyy').toLowerCase().includes(searchTerm.toLowerCase()) ||
        format(new Date(item.date), 'yyyy-MM-dd').includes(searchTerm),
    );
    setFilteredItems(filtered);
  }, [items, searchTerm]);

  const fetchItems = async () => {
    if (hasFetched.current) return; // Prevent duplicate calls
    hasFetched.current = true;

    try {
      console.log('Fetching advancing org for direct report:', selectedAlias);
      const data = await api.getAdvancingOrg(selectedAlias);
      setItems(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching advancing org items:', error);
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleEdit = (item: AdvancingOrg) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this advancing organization item?')) {
      try {
        await api.deleteAdvancingOrg(id);
        hasFetched.current = false; // Reset to allow refresh
        await fetchItems();
      } catch (error) {
        console.error('Error deleting advancing org item:', error);
      }
    }
  };

  const handleSave = async (itemData: Omit<AdvancingOrg, 'id'>) => {
    try {
      if (editingItem) {
        await api.updateAdvancingOrg(editingItem.id, itemData);
      } else {
        await api.createAdvancingOrg(itemData, selectedAlias);
      }
      setIsModalOpen(false);
      hasFetched.current = false; // Reset to allow refresh
      await fetchItems();
    } catch (error) {
      console.error('Error saving advancing org item:', error);
    }
  };

  const handleManagerApprovalChange = async (id: string, approved: boolean) => {
    try {
      await api.updateAdvancingOrgManagerApproval(id, approved);

      // Update local state instead of refetching to maintain sort order
      setItems((prevItems) =>
        prevItems.map((item) => (item.id === id ? { ...item, manager_approved: approved } : item)),
      );
    } catch (error) {
      console.error('Error updating manager approval:', error);
      alert('Error updating manager approval: ' + (error instanceof Error ? error.message : String(error)));
    }
  };

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h2" gutterBottom>
          Advancing Organization
        </Typography>
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Loading advancing organization items...
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h2">Advancing Organization</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd} size="small">
          Add
        </Button>
      </Box>

      <TextField
        fullWidth
        placeholder="Search advancing organization items..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
      />

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ minHeight: '48px' }}>
              <TableCell sx={{ minWidth: '160px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Activity Date
              </TableCell>
              <TableCell sx={{ minWidth: '100px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Alias
              </TableCell>
              <TableCell sx={{ minWidth: '120px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Manager Alias
              </TableCell>
              <TableCell sx={{ minWidth: '150px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Type
              </TableCell>
              <TableCell sx={{ minWidth: '200px', resize: 'horizontal', overflow: 'auto', minHeight: '48px' }}>
                Description
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '120px',
                  textAlign: 'center',
                  resize: 'horizontal',
                  overflow: 'auto',
                  minHeight: '48px',
                }}
              >
                Actions
              </TableCell>
              <TableCell
                sx={{
                  minWidth: '140px',
                  textAlign: 'center',
                  resize: 'horizontal',
                  overflow: 'auto',
                  minHeight: '48px',
                }}
              >
                Manager Validated
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
              <TableRow key={item.id} hover sx={{ resize: 'vertical', overflow: 'auto', minHeight: '60px' }}>
                <TableCell sx={{ minHeight: '60px', verticalAlign: 'top' }}>
                  {format(new Date(item.date), 'MMMM d, yyyy')}
                </TableCell>
                <TableCell sx={{ minHeight: '60px', verticalAlign: 'top' }}>{item.alias}</TableCell>
                <TableCell sx={{ minHeight: '60px', verticalAlign: 'top' }}>{item.manager_alias}</TableCell>
                <TableCell
                  sx={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                    py: 2,
                    minHeight: '60px',
                    verticalAlign: 'top',
                  }}
                >
                  {item.type}
                </TableCell>
                <TableCell
                  sx={{
                    wordWrap: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                    py: 2,
                    minHeight: '60px',
                    verticalAlign: 'top',
                  }}
                >
                  {item.description}
                </TableCell>
                <TableCell sx={{ textAlign: 'center', minHeight: '60px', verticalAlign: 'middle' }}>
                  <IconButton size="small" onClick={() => handleEdit(item)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(item.id)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
                <TableCell sx={{ textAlign: 'center', minHeight: '60px', verticalAlign: 'middle' }}>
                  <Checkbox
                    checked={item.manager_approved}
                    onChange={(e) => handleManagerApprovalChange(item.id, e.target.checked)}
                    color="primary"
                    title="Manager Validated"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredItems.length}
        page={page}
        onPageChange={(event, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={() => {}}
        rowsPerPageOptions={[]}
        sx={{ borderTop: 1, borderColor: 'divider' }}
      />

      {filteredItems.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body2" color="text.secondary">
            {!selectedAlias
              ? 'Please select a direct report to view their advancing org contributions'
              : `No advancing organization items found.${searchTerm ? ' Try adjusting your search terms.' : ''}`}
          </Typography>
        </Box>
      )}

      {isModalOpen && (
        <AdvancingOrgModal item={editingItem} onSave={handleSave} onClose={() => setIsModalOpen(false)} />
      )}
    </Paper>
  );
};

export default AdvancingOrgSection;
